// src/lib/creditHistory.ts
// Pure logica voor het gebruikersgerichte credit-verbruiksoverzicht.
// Geen Supabase-calls hier: datafetching zit in de componenten,
// zodat dit bestand volledig unit-testbaar is.

export interface CreditLogRow {
  id: string;
  session_id: string | null;
  action_type: string;
  credits_used: number;
  success: boolean;
  created_at: string;
}

export interface CreditGroup {
  key: string;
  title: string;
  latestAt: string;
  totalCredits: number;
  rows: CreditLogRow[];
}

// Plain NL, geen tech-jargon of providernamen (user-facing).
const ACTION_LABELS: Record<string, string> = {
  article_generate: 'Artikel geschreven',
  article_regenerate: 'Artikel herschreven',
  titles_generate: 'Titels bedacht',
  intros_generate: 'Intro geschreven',
  images_generate: 'Afbeeldingen gemaakt',
  idea_generate: 'Blogidee bedacht',
  social_generate: 'Social post gemaakt',
  email_campaign_send: 'E-mailcampagne verzonden',
  email_variant_generate: 'E-mailtekst gemaakt',
  email_transactional: 'E-mail verzonden',
  profile_generate: 'Bedrijfsprofiel gemaakt',
  whatsapp_capture: 'WhatsApp-idee verwerkt',
};

export function actionLabel(actionType: string): string {
  return ACTION_LABELS[actionType] ?? 'AI-actie';
}

export function groupCreditLog(
  rows: CreditLogRow[],
  sessionTitles: Record<string, string>,
): CreditGroup[] {
  const relevant = rows.filter((r) => r.success && r.credits_used > 0);
  const byKey = new Map<string, CreditGroup>();

  for (const r of relevant) {
    // Losse acties (geen sessie) worden gebundeld per actie per dag,
    // zodat bijv. tien keer beeldgeneratie op een dag een regel wordt.
    const key = r.session_id ?? `los-${r.action_type}-${r.created_at.slice(0, 10)}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.rows.push(r);
      existing.totalCredits += r.credits_used;
      if (r.created_at > existing.latestAt) existing.latestAt = r.created_at;
    } else {
      byKey.set(key, {
        key,
        title: r.session_id
          ? (sessionTitles[r.session_id] ?? 'Blog (zonder titel)')
          : actionLabel(r.action_type),
        latestAt: r.created_at,
        totalCredits: r.credits_used,
        rows: [r],
      });
    }
  }

  return [...byKey.values()].sort((a, b) => b.latestAt.localeCompare(a.latestAt));
}

export function billingPeriodStart(nextBillingDate: string | null, now: Date): Date {
  if (nextBillingDate) {
    const start = new Date(nextBillingDate);
    start.setUTCMonth(start.getUTCMonth() - 1);
    // Verlopen billing date levert een start in de toekomst of een
    // periode ouder dan een maand op; dan kalendermaand als vangnet.
    if (start <= now && new Date(nextBillingDate) > now) {
      return start;
    }
  }
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

export function usedInPeriod(rows: CreditLogRow[], periodStart: Date): number {
  // Numeriek vergelijken: Supabase levert timestamptz als '+00:00',
  // toISOString() geeft 'Z'; stringvergelijking gaat dan mis op de grens.
  const startMs = periodStart.getTime();
  return rows
    .filter((r) => r.success && r.credits_used > 0 && Date.parse(r.created_at) >= startMs)
    .reduce((sum, r) => sum + r.credits_used, 0);
}
